/**
 * MatterTimeEntryPanel — one time entry, opened by clicking its row.
 *
 * The list could only be operated through the row's kebab menu, and clicking
 * the row itself did nothing — so reading an entry meant opening the edit
 * modal and reading the form. This panel is the entry's own surface: who
 * logged it and against what, then the notes on it, then editing in place.
 *
 * NOTES / COMMENTS — FLAGGED.
 * There is no time-entry comment endpoint on the backend (the entry carries a
 * single `description`), so a comment is appended to the description as a
 * dated, attributed line and persisted through the existing PATCH. That gives
 * a real, shared, server-side thread with no data-layer change, but it is not
 * a proper thread: no per-comment author id, edit or delete. A dedicated
 * `/matters/<uuid>/time-entries/<id>/comments` route would replace the parsing
 * here and nothing else.
 */

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PiChatCircleLight,
  PiClockLight,
  PiPencilSimpleLineLight,
  PiTrashLight,
} from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import {
  updateTimeEntry,
  TimeEntryWritePayload,
} from '../../../store/actions/economicsActions';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Toggle from '../../shared/TailwindComponents/Toggle';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, FieldGrid, Well } from '../../shared/brand/Field';
import { FIELD_LABEL_CLASS, FIELD_HELP_CLASS } from '../../shared/TailwindComponents/FieldShell';
import UserCell from './UserCell';
import { ymdToDate, dateToYmd } from './dateHelpers';
import { TimeEntryValue, getTimeEntryErrors } from './economicsConstants';
import type { ActivityType, TimeEntry } from '../../../types/economics';

export interface MatterTimeEntryPanelProps {
  entry: TimeEntry;
  matterUuid: string;
  activityTypes: ActivityType[];
  /** Label for the entry's user when the row carries no resolvable name. */
  fallbackLabel?: string;
  /** Whether this viewer may edit/delete the entry. */
  canManage: boolean;
  /**
   * Whether the footer offers Delete. The matter Time tab owns the delete
   * confirmation and the reload, so the personal timesheet (which has neither)
   * opens the same panel for reading and correcting only.
   */
  allowDelete?: boolean;
  /** Re-read the list after a write. */
  onSaved: () => void | Promise<void>;
  /** Hand the delete back to the list, which owns the confirmation. */
  onDelete: (entry: TimeEntry) => void;
  onClose: () => void;
}

/** `[2026-08-11 · Ada Lovelace] text` — one appended note line. */
const NOTE_LINE = /^\[(\d{4}-\d{2}-\d{2})\s+·\s+([^\]]+)\]\s*(.*)$/;

export interface ParsedNotes {
  /** The entry's own description, without the appended note lines. */
  description: string;
  comments: Array<{ date: string; author: string; text: string }>;
}

/** Split a description into its base text and the note lines appended to it. */
export const parseEntryNotes = (raw?: string | null): ParsedNotes => {
  const comments: ParsedNotes['comments'] = [];
  const kept: string[] = [];
  (raw || '').split('\n').forEach((line) => {
    const match = NOTE_LINE.exec(line.trim());
    if (match) comments.push({ date: match[1], author: match[2], text: match[3] });
    else kept.push(line);
  });
  return { description: kept.join('\n').trim(), comments };
};

/** Append one note line to a description, keeping the base text first. */
export const appendEntryNote = (
  raw: string | null | undefined,
  date: string,
  author: string,
  text: string,
): string => {
  const line = `[${date} · ${author}] ${text.replace(/\s*\n\s*/g, ' ').trim()}`;
  const base = (raw || '').trim();
  return base ? `${base}\n${line}` : line;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const MatterTimeEntryPanel: React.FC<MatterTimeEntryPanelProps> = ({
  entry,
  matterUuid,
  activityTypes,
  fallbackLabel,
  canManage,
  allowDelete = true,
  onSaved,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  /** The panel owns the entry once it has written to it, so it never shows a stale figure. */
  const [current, setCurrent] = useState<TimeEntry>(entry);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ entry_date?: string; hours?: string }>({});

  const parsed = useMemo(() => parseEntryNotes(current.description), [current.description]);

  const activityLabel = useMemo(() => {
    const found = activityTypes.find((a) => a.id === current.activity_type_id);
    return found ? t(found.label) : undefined;
  }, [activityTypes, current.activity_type_id, t]);

  // The payload spells the name both ways depending on the endpoint that
  // last wrote it, so both are read before falling back to the address.
  const authorName =
    [userInfo?.FirstName || userInfo?.first_name, userInfo?.LastName || userInfo?.last_name]
      .filter(Boolean)
      .join(' ')
    || userInfo?.FullName
    || userInfo?.Email
    || userInfo?.email
    || t('Unknown');

  const [draft, setDraft] = useState<TimeEntryValue>({
    entry_date: current.entry_date || todayStr(),
    hours: String(current.hours_decimal ?? ''),
    activity_type_id: current.activity_type_id != null ? String(current.activity_type_id) : '',
    billable: current.billable ?? true,
    description: parsed.description,
  });

  const startEdit = () => {
    setDraft({
      entry_date: current.entry_date || todayStr(),
      hours: String(current.hours_decimal ?? ''),
      activity_type_id: current.activity_type_id != null ? String(current.activity_type_id) : '',
      billable: current.billable ?? true,
      description: parseEntryNotes(current.description).description,
    });
    setErrors({});
    setEditing(true);
  };

  /** One write path for both the edit form and the comment composer. */
  const persist = async (payload: Partial<TimeEntryWritePayload>) => {
    setSaving(true);
    try {
      const updated = await dispatch(updateTimeEntry(matterUuid, current.id, payload));
      setCurrent((prev) => ({ ...prev, ...(updated || {}), ...payload } as TimeEntry));
      await onSaved();
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    const found = getTimeEntryErrors(draft, todayStr());
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    // The appended notes are not part of the form, so they are re-attached to
    // whatever description the user just wrote — editing must not eat them.
    const before = parseEntryNotes(current.description);
    const notes = before.comments
      .map((c) => `[${c.date} · ${c.author}] ${c.text}`)
      .join('\n');
    let nextDescription = [draft.description.trim(), notes].filter(Boolean).join('\n');
    // CHANGE LOG — FLAGGED. There is no audit endpoint for a time entry and the
    // row carries no `updated_at`, so a description correction would otherwise
    // leave no trace of who changed it or when. One attributed line is appended
    // to the same note stream the comments use: a real, shared, server-side
    // record with no data-layer change. A proper audit row on the entry would
    // replace this and nothing else.
    if (before.description.trim() !== draft.description.trim()) {
      nextDescription = appendEntryNote(
        nextDescription,
        todayStr(),
        authorName,
        t('Edited the description.'),
      );
    }
    const ok = await persist({
      entry_date: draft.entry_date,
      hours_decimal: Number(draft.hours),
      activity_type_id: draft.activity_type_id ? Number(draft.activity_type_id) : null,
      billable: draft.billable,
      description: nextDescription || undefined,
    });
    if (ok) setEditing(false);
  };

  const addComment = async () => {
    const text = comment.trim();
    if (!text) return;
    const next = appendEntryNote(current.description, todayStr(), authorName, text);
    const ok = await persist({ description: next });
    if (ok) setComment('');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto custom-scrollbar-main p-5 space-y-4">
        {/* ── Who and how much ─────────────────────────────────── */}
        <GlassCard icon={<PiClockLight />} title={t('Time entry')}>
          <div className="flex items-center justify-between gap-3">
            <UserCell user={current} fallbackLabel={fallbackLabel} />
            <span className="flex items-center gap-2 flex-shrink-0">
              {current.billable
                ? <Badge color="green">{t('Billable')}</Badge>
                : <Badge color="gray">{t('Non-billable')}</Badge>}
              <span className="text-lg font-semibold tabular-nums text-fg-strong">
                {Number(current.hours_decimal || 0).toFixed(2)} {t('h')}
              </span>
            </span>
          </div>
        </GlassCard>

        {editing ? (
          <GlassCard icon={<PiPencilSimpleLineLight />} title={t('Edit time entry')}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DateTimePicker
                  id="entry-panel-date"
                  label={t('Date')}
                  selected={ymdToDate(draft.entry_date)}
                  placeholder={t('Date')}
                  showTimeSelect={false}
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                  fullWidth
                  required
                  error={errors.entry_date ? t(errors.entry_date) : undefined}
                  onChange={(d) => setDraft((p) => ({ ...p, entry_date: dateToYmd(d) }))}
                />
                <Input
                  type="number"
                  id="entry-panel-hours"
                  label={t('Hours')}
                  placeholder="0.00"
                  min={0}
                  value={draft.hours}
                  onChange={(e) => setDraft((p) => ({ ...p, hours: e.target.value }))}
                  error={errors.hours ? t(errors.hours) : undefined}
                  required
                />
              </div>
              <Dropdown
                id="entry-panel-activity"
                label={t('Activity')}
                options={activityTypes.map((a) => ({ value: String(a.id), label: t(a.label) }))}
                value={draft.activity_type_id}
                placeholder={t('Select an activity')}
                onChange={(v) => setDraft((p) => ({ ...p, activity_type_id: Array.isArray(v) ? v[0] : v }))}
                fullWidth
                className="w-full"
              />
              <div className="flex items-center justify-between">
                <div>
                  <p className={FIELD_LABEL_CLASS}>{t('Billable')}</p>
                  <p className={FIELD_HELP_CLASS}>
                    {t('Counts toward billable revenue for this matter.')}
                  </p>
                </div>
                <Toggle enabled={draft.billable} onToggle={(billable) => setDraft((p) => ({ ...p, billable }))} />
              </div>
              <Textarea
                id="entry-panel-description"
                label={t('Description')}
                value={draft.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDraft((p) => ({ ...p, description: e.target.value }))}
                placeholder={t('What did you work on?')}
                rows={3}
              />
            </div>
          </GlassCard>
        ) : (
          <GlassCard icon={<PiClockLight />} title={t('Details')}>
            <div className="space-y-4">
              <FieldGrid columns={2}>
                <Field label={t('Date')} value={current.entry_date} />
                <Field label={t('Activity')} value={activityLabel} />
              </FieldGrid>
              <Field label={t('Description')} value={parsed.description} />
            </div>
          </GlassCard>
        )}

        {/* ── Comments ─────────────────────────────────────────── */}
        <GlassCard icon={<PiChatCircleLight />} title={t('Comments')}>
          <div className="space-y-3">
            {parsed.comments.length === 0 ? (
              <p className="text-sm text-fg-muted">{t('No comments yet.')}</p>
            ) : (
              <ul className="space-y-2">
                {parsed.comments.map((c, i) => (
                  <li key={`${c.date}-${i}`}>
                    <Well className="space-y-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-xs font-medium text-fg-body">{c.author}</span>
                        <span className="text-[11px] text-fg-muted tabular-nums">{c.date}</span>
                      </div>
                      <p className="text-sm text-fg-body whitespace-pre-wrap">{c.text}</p>
                    </Well>
                  </li>
                ))}
              </ul>
            )}

            {canManage && (
              <div className="space-y-2">
                <Textarea
                  id="entry-panel-comment"
                  label={t('Add a comment')}
                  value={comment}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                  placeholder={t('Add a note about this entry')}
                  rows={2}
                />
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={addComment}
                    disabled={saving || !comment.trim()}
                  >
                    {t('Comment')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* ── Operations ─────────────────────────────────────────── */}
      {canManage && (
        <div className={`border-t border-hairline-soft px-5 py-4 flex gap-3 ${allowDelete ? 'justify-between' : 'justify-end'}`}>
          {allowDelete && (
            <Button
              variant="destructive"
              size="small"
              icon={<PiTrashLight />}
              onClick={() => { onDelete(current); onClose(); }}
              disabled={saving}
            >
              {t('Delete')}
            </Button>
          )}
          {editing ? (
            <span className="flex gap-2">
              <Button variant="neutral" size="small" onClick={() => setEditing(false)} disabled={saving}>
                {t('Cancel')}
              </Button>
              <Button variant="primary" size="small" onClick={saveEdit} disabled={saving}>
                {saving ? t('Saving...') : t('Save')}
              </Button>
            </span>
          ) : (
            <Button
              variant="primary"
              size="small"
              icon={<PiPencilSimpleLineLight />}
              onClick={startEdit}
              disabled={saving}
            >
              {t('Edit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MatterTimeEntryPanel;
